<template>
  <a-modal :title="modalTitle" :visible="visible" :confirm-loading="loading" @ok="handleOk" @cancel="handleCancel"
    @afterClose="closeModal" okText="确认" cancelText="取消" width="500px" destroyOnClose>
    <a-form ref="formRef" :model="formState" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">

      <a-form-item :label="nameLabel" name="name" :rules="[{ required: true, message: '请输入' + nameLabel }]">
        <a-input v-model:value="formState.name" :placeholder="'请输入' + nameLabel" />
      </a-form-item>

      <!-- linkType：点击跳转目标，在选择商品上方，可在新增/编辑时修改 -->
      <a-form-item label="跳转类型" name="linkType">
        <a-select v-model:value="formState.linkType">
          <a-select-option v-for="t in linkTypes" :key="t" :value="t">{{ t }}</a-select-option>
        </a-select>
      </a-form-item>

      <!-- 仅当跳转类型为“查看商品”时需要产品编号（选择跳转商品，选择后展示图文） -->
      <a-form-item v-if="formState.linkType === '查看商品'" label="跳转商品" name="value"
        :rules="[{ required: true, message: '请选择跳转商品' }]">
        <a-space>
          <a-button @click="showProductSelect">选择商品</a-button>
          <a-image v-if="formState.productThumbnail" :src="thumbnailUrl + formState.productThumbnail" :width="48"
            :height="48" style="object-fit: cover; border-radius: 4px;" alt="商品图" />
          <span>{{ formState.productName || '未选择商品' }}</span>
        </a-space>
      </a-form-item>

      <a-form-item :label="imageLabel" name="imageUuid">
        <a-upload v-model:file-list="fileList" name="file" list-type="picture-card" :show-upload-list="false"
          :custom-request="uploadImage" :before-upload="beforeUpload" class="upload-wrapper">
          <div v-if="formState.imageUuid" class="upload-preview">
            <img :src="imageUrl + formState.imageUuid" alt="图片" />
            <div class="upload-actions" @click.stop="removeImage">
              <DeleteOutlined />
            </div>
          </div>
          <div v-else>
            <loading-outlined v-if="uploading"></loading-outlined>
            <plus-outlined v-else></plus-outlined>
            <div class="ant-upload-text">上传图片</div>
          </div>
        </a-upload>
      </a-form-item>

      <a-form-item v-if="isEdit" label="ID" name="id" style="display: none;">
        <a-input v-model:value="formState.id" disabled />
      </a-form-item>

    </a-form>
  </a-modal>

  <!-- Banner 选择跳转商品 -->
  <ProductSelectModal v-model:visible="productSelectVisible" @select="handleProductSelect" />
</template>

<script>
import { PlusOutlined, LoadingOutlined, DeleteOutlined } from "@ant-design/icons-vue";
import { AddMallSetting, UpdateMallSetting } from "/src/api/shopping.js";
import { Upload } from "/src/api/upload.js";
import { message } from "ant-design-vue";
import ProductSelectModal from "./ProductSelectModal.vue";

export default {
  name: "商城配置新增编辑弹窗",
  components: {
    PlusOutlined,
    LoadingOutlined,
    DeleteOutlined,
    ProductSelectModal,
  },
  props: {
    visible: { type: Boolean, default: false },
    record: { type: Object, default: () => ({}) },
    category: { type: String, default: "Banner" },
    categoryConfig: { type: Object, default: () => ({}) },
  },
  emits: ['update:visible', 'ok'],
  data() {
    return {
      loading: false,
      uploading: false,
      imageUrl: '/api/file/img/',
      thumbnailUrl: '/api/file/img/thumbnail/',
      fileList: [],
      linkTypes: ['查看商品', '商品列表', '查看大图'],
      productSelectVisible: false,
      formState: {
        id: undefined,
        name: '',
        value: '',
        imageUuid: '',
        linkType: '',
        productName: '',
        productThumbnail: '',
      },
      formRef: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.formState.id;
    },
    isEnterprise() {
      return !!this.categoryConfig.isEnterprise;
    },
    nameLabel() {
      return this.categoryConfig.nameLabel || '名称';
    },
    imageLabel() {
      return this.categoryConfig.imageLabel || '图片';
    },
    modalTitle() {
      const label = this.categoryConfig.label || '';
      return (this.isEdit ? "编辑" : "新增") + label;
    },
  },
  watch: {
    record: {
      handler(newRecord) {
        if (newRecord && newRecord.id) {
          this.formState = {
            id: newRecord.id,
            name: newRecord.name || '',
            value: newRecord.value || '',
            // 企业资质：图片 uuid 保存在 value 中
            imageUuid: newRecord.imageUuid || (this.isEnterprise ? (newRecord.value || '') : ''),
            linkType: newRecord.linkType || this.categoryConfig.linkType || '查看商品',
            productName: newRecord.product.name || '',
            productThumbnail: newRecord.product.thumbnailUuid || '',
          };
        } else {
          this.resetFormState();
        }
      },
      immediate: true,
      deep: true,
    }
  },
  methods: {
    resetFormState() {
      this.formState = {
        id: undefined,
        name: '',
        value: '',
        imageUuid: '',
        linkType: this.categoryConfig.linkType || '查看商品',
        productName: '',
        productThumbnail: '',
      };
      this.fileList = [];
      this.uploading = false;
      this.productSelectVisible = false;
    },

    removeImage() {
      this.formState.imageUuid = '';
      this.fileList = [];
      message.success('图片已移除');
    },

    beforeUpload(file) {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        message.error('只能上传 JPG/PNG 文件!');
      }
      return isJpgOrPng;
    },

    async uploadImage({ file, onSuccess, onError }) {
      this.uploading = true;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'portrait');
      formData.append('system', 'true');

      try {
        const res = await Upload(formData);
        if (res.errCode === 0 && res.data) {
          this.formState.imageUuid = res.data;
          message.success('图片上传成功');
          onSuccess(res);
        } else {
          message.error('上传失败：' + (res.message || '未知错误'));
          onError(new Error('Upload failed'));
        }
      } catch (error) {
        console.error("uploadImage error:", error);
        message.error('网络错误，上传失败');
        onError(error);
      } finally {
        this.uploading = false;
      }
    },

    async handleOk() {
      try {
        await this.$refs.formRef.validate();
        this.loading = true;

        // 组装统一接口参数
        const payload = {
          id: this.formState.id,
          name: this.formState.name,
          imageUuid: this.formState.imageUuid,
          category: this.category,
          linkType: this.formState.linkType, // 可在新增/编辑时修改的跳转类型
        };
        // 企业资质：value 保存照片 uuid（业务数据）
        if (this.isEnterprise) {
          payload.value = this.formState.imageUuid;
        }
        // 促销品类 / 商城产品类别：value 与 name 相同
        else if (this.category === '促销品类' || this.category === '商城产品类别') {
          payload.value = this.formState.name;
        }
        // 查看商品跳转（如 Banner）：value 保存产品编号
        else if (this.formState.linkType === '查看商品') {
          payload.value = this.formState.value;
        }

        let res;
        if (this.isEdit) {
          res = await UpdateMallSetting(payload);
        } else {
          delete payload.id;
          res = await AddMallSetting(payload);
        }

        if (res.errCode === 0) {
          message.success("操作成功");
          this.resetFormState();
          this.$emit('ok');
          this.$emit('update:visible', false);
        } else {
          message.error(res.errMsg || "操作失败");
        }
      } catch (error) {
        if (error && error.errorFields && error.errorFields.length > 0) {
          const firstErrorMsg = error.errorFields[0].errors[0];
          message.warning(`请检查表单：${firstErrorMsg}`);
        } else if (error !== false) {
          console.error('API Error:', error);
          message.error('请求异常，请稍后重试');
        }
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$emit('update:visible', false);
    },

    closeModal() {
      this.resetFormState();
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields();
      }
    },

    // 打开商品选择弹窗（Banner）
    showProductSelect() {
      this.productSelectVisible = true;
    },

    // 选择商品：value 保存产品 uuid，并记录商品名称与缩略图（用于展示图文）
    handleProductSelect(record) {
      if (record) {
        this.formState.value = record.uuid;
        this.formState.productName = record.name || '';
        this.formState.productThumbnail = record.thumbnailUuid || '';
      }
    },
  },
};
</script>

<style scoped>
.upload-wrapper {
  width: 104px;
  height: 104px;
}

.upload-preview {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.upload-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: 5;
}

.upload-preview:hover .upload-actions {
  opacity: 1;
}
</style>
